import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { Label } from "../components/ui/label"
import { Button } from "../components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../components/ui/select"
import { CheckSquare } from 'lucide-react'

const questions: string[] = [
  "会議やグループディスカッションで、自分の意見を積極的に発言しますか？",
  "新しいアイデアや提案を他人に進んで伝えますか？",
  "問題や対立を避けずに解決しようとしますか？",
  "自分の意見や要求をはっきりと伝えることができますか？",
  "リーダーシップを発揮することに自信がありますか？",
  "チームのリーダーを任されることが多いですか？",
  "新しいプロジェクトや挑戦を率先して引き受けますか？",
  "交渉や説得に自信がありますか？",
  "自分の意見や立場を他人に納得させるのが得意ですか？",
  "重要な決定を迅速に下すことができますか？",
  "喜怒哀楽の感情を素直に表現するほうですか？",
  "他人の感情に敏感で共感しやすいですか？",
  "自分の感情を抑えることなく表現しますか？",
  "人と感情を共有することが好きですか？",
  "感情的な状況でも冷静に対処できますか？",
  "ストレスの多い状況でも冷静さを保てますか？",
  "感情を上手にコントロールしながら仕事に取り組めますか？",
  "対人関係において、他人の感情を尊重することが得意ですか？",
  "自分の感情をオープンに表現することに抵抗はありませんか？",
  "感情的なフィードバックを他人に適切に伝えられますか？",
];

const scoreOptions: { value: string; label: string }[] = [
  { value: "1", label: "全く当てはまらない" },
  { value: "2", label: "あまり当てはまらない" },
  { value: "3", label: "どちらとも言えない" },
  { value: "4", label: "ある程度当てはまる" },
  { value: "5", label: "非常に当てはまる" },
];

interface StyleDetail {
  特徴: string;
  行動: string;
  強み: string;
  コミュニケーションのコツ: string;
}

interface StyleDetails {
  [key: string]: StyleDetail;
}

const styleDetails: StyleDetails = {
  エクスプレッシブ: {
    特徴: "高自己主張、高感情表現",
    行動: "感情豊かで社交的、話すことが好き",
    強み: "アイデアや創造性に富む",
    コミュニケーションのコツ: "興味を持って話を聞き、共感を示す"
  },
  ドライビング: {
    特徴: "高自己主張、低感情表現",
    行動: "目標志向で迅速な決断、結果を重視",
    強み: "決断力があり、行動が早い",
    コミュニケーションのコツ: "簡潔に要点を伝え、結果に焦点を当てる"
  },
  エミアブル: {
    特徴: "低自己主張、高感情表現",
    行動: "協力的で他人の意見を尊重、対立を避ける",
    強み: "人間関係を大切にし、穏やかで親しみやすい",
    コミュニケーションのコツ: "相手の意見を尊重し、安心感を与える"
  },
  アナリティカル: {
    特徴: "低自己主張、低感情表現",
    行動: "データや事実に基づいた判断、慎重",
    強み: "論理的で詳細な分析を好む",
    コミュニケーションのコツ: "具体的なデータや事実を用いて、論理的に話す"
  }
};

interface SocialStyleMatrixProps {
  assertivenessScore: number;
  responsivenessScore: number;
}

const SocialStyleMatrix: React.FC<SocialStyleMatrixProps> = ({ assertivenessScore, responsivenessScore }) => {
  const styles = {
    cell: "border-2 border-black p-2 text-center rounded-lg transition-all duration-300 ease-in-out mb-2",
    highlighted: "bg-yellow-300 font-bold transform scale-105 shadow-lg",
  };

  const isHighAssertiveness = assertivenessScore >= 25;
  const isHighResponsiveness = responsivenessScore >= 25;

  return (
    <div className="mt-4">
      <h4 className="font-bold text-lg mb-2 text-center text-black">ソーシャルスタイルマトリクス</h4>
      <div className="flex flex-col">
        <div className={`${styles.cell} ${isHighAssertiveness && isHighResponsiveness ? styles.highlighted : ''}`}>
          エクスプレッシブ
        </div>
        <div className={`${styles.cell} ${isHighAssertiveness && !isHighResponsiveness ? styles.highlighted : ''}`}>
          ドライビング
        </div>
        <div className={`${styles.cell} ${!isHighAssertiveness && isHighResponsiveness ? styles.highlighted : ''}`}>
          エミアブル
        </div>
        <div className={`${styles.cell} ${!isHighAssertiveness && !isHighResponsiveness ? styles.highlighted : ''}`}>
          アナリティカル
        </div>
      </div>
    </div>
  );
};

interface Result {
  assertivenessScore: number;
  responsivenessScore: number;
  style: keyof StyleDetails;
}

const SocialStyleAssessment: React.FC = () => {
  const [scores, setScores] = useState<string[]>(Array(20).fill(''));
  const [result, setResult] = useState<Result | null>(null);

  const handleScoreChange = (index: number, value: string) => {
    const newScores = [...scores];
    newScores[index] = value;
    setScores(newScores);
  };

  const calculateResult = () => {
    const assertivenessScore = scores.slice(0, 10).reduce((sum, score) => sum + parseInt(score || '0'), 0);
    const responsivenessScore = scores.slice(10).reduce((sum, score) => sum + parseInt(score || '0'), 0);

    let style: keyof StyleDetails;
    if (assertivenessScore >= 25 && responsivenessScore >= 25) {
      style = 'エクスプレッシブ';
    } else if (assertivenessScore >= 25 && responsivenessScore < 25) {
      style = 'ドライビング';
    } else if (assertivenessScore < 25 && responsivenessScore >= 25) {
      style = 'エミアブル';
    } else {
      style = 'アナリティカル';
    }

    setResult({
      assertivenessScore,
      responsivenessScore,
      style
    });
  };

  return (
    <Card className="w-full max-w-md md:max-w-2xl mx-auto bg-gradient-to-br from-pink-100 to-blue-100 shadow-xl">
      <CardHeader className="bg-purple-500 text-white rounded-t-lg">
        <CardTitle className="text-2xl font-bold text-center">ソーシャルスタイル編</CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        <div className="space-y-4">
          {questions.map((question, index) => (
            <div key={index} className="space-y-2 bg-white p-4 rounded-lg shadow-md">
              <Label htmlFor={`question-${index}`} className="text-black">{`${index + 1}. ${question}`}</Label>
              <Select onValueChange={(value: string) => handleScoreChange(index, value)}>
                <SelectTrigger className="w-full mt-1 border-2 border-black rounded-lg text-black">
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {scoreOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ))}
          <Button 
            onClick={calculateResult} 
            className="w-full bg-pink-500 hover:bg-pink-600 text-white font-bold py-3 px-4 rounded-lg transition duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center h-12"
          >
            <span className="flex items-center">
              <CheckSquare className="mr-2" size={18} />
              <span>診断結果を表示</span>
            </span>
          </Button>
          {result && (
              <div className="mt-8 p-6 bg-white rounded-lg shadow-lg border-2 border-black">
                <h3 className="font-bold text-2xl mb-4 text-center text-black">診断結果</h3>
                <div className="space-y-4">
                  <div>
                    <p className="text-lg text-center text-black">自己主張スコア: <span className="font-bold">{result.assertivenessScore}</span></p>
                    <p className="text-lg text-center text-black">感情表現スコア: <span className="font-bold">{result.responsivenessScore}</span></p>
                    <div className="text-center mt-4">
                      <p className="text-xl text-black">あなたのソーシャルスタイル</p>
                      <p className="text-2xl font-bold text-black">{result.style}</p>
                    </div>
                  </div>
                  <SocialStyleMatrix 
                    assertivenessScore={result.assertivenessScore} 
                    responsivenessScore={result.responsivenessScore}
                  />
                <div className="mt-6">
                  <h4 className="font-bold text-xl mb-2 text-black">{result.style}の特徴</h4>
                  <ul className="list-disc pl-5 space-y-2">
                    <li><strong>特徴:</strong> {styleDetails[result.style].特徴}</li>
                    <li><strong>行動:</strong> {styleDetails[result.style].行動}</li>
                    <li><strong>強み:</strong> {styleDetails[result.style].強み}</li>
                    <li><strong>コミュニケーションのコツ:</strong> {styleDetails[result.style].コミュニケーションのコツ}</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default SocialStyleAssessment;